import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import CopyableTypography from "components/Typography/CopyableTypography";
import React from "react";

import { TruncatedText } from "./TruncatedText";

// 14px when using the small prop, 16px by default
const labelFontSize = "0.75rem";
// labels are not rendered when using the small prop
const getValueFontSize = (small: boolean) => (small ? "0.875rem" : "1rem");

export function DisplayOnlyField({
  text,
  subtext,
  label,
  small = false,
  renderValue,
  copyable = true,
  infoTooltip,
  labelSpacing = 0,
  wrapValue = false,
  warningIcon = false,
  warningTooltip,
  groupingKey = false,
  groupingKeyTooltip,
}: {
  text?: string;
  subtext?: string | undefined | null;
  label?: React.ReactNode;
  small?: boolean;
  renderValue?: (value: React.ReactNode) => React.ReactNode;
  copyable?: boolean;
  infoTooltip?: React.ReactNode;
  /** Spacing between label and value in MUI spacing units. Defaults to 0. */
  labelSpacing?: number;
  /** When true, the value text wraps instead of truncating with ellipsis. Defaults to false. */
  wrapValue?: boolean;
  /** When true, shows a warning icon beside the label. */
  warningIcon?: boolean;
  /** Tooltip shown on the warning icon (e.g. an explanation of the warning). */
  warningTooltip?: React.ReactNode;
  /** When true, shows an info icon beside the label to mark this as the single
   * field whose value identified and grouped this record. */
  groupingKey?: boolean;
  /** Tooltip shown on the grouping-key icon (e.g. what the grouping means). */
  groupingKeyTooltip?: React.ReactNode;
}) {
  const valueFontSize = getValueFontSize(small);

  const copyableText = text ? (
    !copyable ? (
      <TruncatedText
        text={text}
        subtext={subtext}
        fontSize={valueFontSize}
        showHoverEffect={false}
      />
    ) : (
      <CopyableTypography
        text={text}
        subtext={subtext}
        sx={{
          fontSize: valueFontSize,
          ...(wrapValue
            ? { whiteSpace: "normal", wordBreak: "break-word" }
            : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
        }}
        tooltipOptions={{
          disableHoverMessage: true,
        }}
      />
    )
  ) : (
    <Typography sx={{ fontSize: valueFontSize, minHeight: valueFontSize }} />
  );

  const value = renderValue ? renderValue(copyableText) : copyableText;

  return (
    <Stack spacing={labelSpacing}>
      {(label || infoTooltip || warningIcon || groupingKey) && (
        <Typography
          variant="body2"
          color="text.secondary"
          fontSize={labelFontSize}
          sx={{ display: "flex", alignItems: "center" }}
        >
          {/* The label text truncates; adornment icons keep their size so they
              are never clipped in narrow columns. */}
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Box>
          {warningIcon && (
            <Tooltip title={warningTooltip ?? ""} placement="top">
              <ErrorOutline
                sx={{ fontSize: "1rem", ml: "0.25rem", color: "warning.main", flexShrink: 0 }}
                aria-label="Warning"
              />
            </Tooltip>
          )}
          {groupingKey && (
            <Tooltip title={groupingKeyTooltip ?? ""} placement="top">
              <InfoOutlined
                sx={{ fontSize: "0.875rem", ml: "0.25rem", color: "action.active", flexShrink: 0 }}
                aria-label="Grouped on this field"
              />
            </Tooltip>
          )}
          {infoTooltip && (
            <InfoIconTooltip
              fontSize="1rem"
              iconButtonSx={{
                ml: "0.25rem",
                padding: "1px",
                flexShrink: 0,
              }}
              tooltip={infoTooltip}
            />
          )}
        </Typography>
      )}
      <Box title={text}>{value}</Box>
    </Stack>
  );
}

export function DisplayOnlyFieldList({
  items,
  label,
  small = false,
  copyable = true,
}: {
  items: string[];
  label?: string | React.ReactNode;
  small?: boolean;
  copyable?: boolean;
}) {
  const valueFontSize = getValueFontSize(small);
  return (
    <Stack spacing={0}>
      {label && (
        <Typography variant="body2" color="text.secondary" fontSize={labelFontSize}>
          {label}
        </Typography>
      )}
      <Stack>
        {items.map((item, index) =>
          copyable ? (
            <CopyableTypography
              key={index}
              text={item}
              sx={{ fontSize: valueFontSize }}
              tooltipOptions={{
                disableHoverMessage: true,
              }}
            />
          ) : (
            <TruncatedText key={index} text={item} fontSize={valueFontSize} />
          )
        )}
      </Stack>
    </Stack>
  );
}
